import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import logoLight from '../../../assets/images/logo2.png';
import logoDark from '../../../assets/images/logo.png';

const NAV_LINKS = [
  { label: 'Inicio', to: '/' },
  { label: 'Productos', to: '/productos' },
  { label: 'Nosotros', href: '/#nosotros' },
  { label: 'Contacto', href: '/#contacto' },
];

function Navbar({ checkScroll = true }) {
  // "solid" = opaque header (scrolled, hovered, menu open, or an inner page)
  const [atTop, setAtTop] = useState(checkScroll);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!checkScroll) return;
    const onScroll = () => setAtTop(window.scrollY < 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [checkScroll]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const transparent = atTop && !open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        transparent
          ? 'bg-transparent'
          : 'bg-white/90 shadow-[0_1px_0_0_rgba(20,19,15,0.06)] backdrop-blur-md'
      }`}
    >
      {/* Thin brand accent line */}
      <div className="h-[3px] w-full bg-gradient-to-r from-brand-400 via-brand-500 to-brand-300" />

      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-10">
        {/* Logo */}
        <Link to="/" aria-label="Fabrilum — inicio" className="shrink-0">
          <img
            src={transparent ? logoLight : logoDark}
            alt="Fabrilum"
            className="h-9 w-auto md:h-10"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => {
            const cls = `group relative text-sm font-medium tracking-wide transition-colors ${
              transparent ? 'text-white/90 hover:text-white' : 'text-ink hover:text-brand-600'
            }`;
            const underline = (
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-current transition-all duration-300 group-hover:w-full" />
            );
            return l.to ? (
              <Link key={l.label} to={l.to} className={cls}>
                {l.label}
                {underline}
              </Link>
            ) : (
              <a key={l.label} href={l.href} className={cls}>
                {l.label}
                {underline}
              </a>
            );
          })}
        </nav>

        {/* Desktop CTA */}
        <a
          href="https://wa.me/+5493515395915?text=Quiero%20hacer%20un%20pedido"
          target="_blank"
          rel="noopener noreferrer"
          className={`hidden rounded-full px-6 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 lg:inline-flex ${
            transparent
              ? 'border border-white/40 text-white hover:bg-white hover:text-ink'
              : 'bg-ink text-white hover:bg-brand-500'
          }`}
        >
          Hacer un pedido
        </a>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Abrir menú"
          aria-expanded={open}
          className="relative flex h-10 w-10 flex-col items-center justify-center gap-[5px] lg:hidden"
        >
          <span
            className={`h-0.5 w-6 rounded-full transition-all duration-300 ${
              transparent ? 'bg-white' : 'bg-ink'
            } ${open ? 'translate-y-[7px] rotate-45' : ''}`}
          />
          <span
            className={`h-0.5 w-6 rounded-full transition-all duration-300 ${
              transparent ? 'bg-white' : 'bg-ink'
            } ${open ? 'opacity-0' : ''}`}
          />
          <span
            className={`h-0.5 w-6 rounded-full transition-all duration-300 ${
              transparent ? 'bg-white' : 'bg-ink'
            } ${open ? '-translate-y-[7px] -rotate-45' : ''}`}
          />
        </button>
      </div>

      {/* Mobile menu panel */}
      <div
        className={`overflow-hidden bg-white lg:hidden ${
          open ? 'max-h-[80vh] border-t border-ink/5' : 'max-h-0'
        } transition-[max-height] duration-500 ease-in-out`}
      >
        <nav className="flex flex-col px-6 py-4">
          {NAV_LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="border-b border-ink/5 py-4 text-lg font-light text-ink transition-colors hover:text-brand-600"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="border-b border-ink/5 py-4 text-lg font-light text-ink transition-colors hover:text-brand-600"
              >
                {l.label}
              </a>
            ),
          )}
          <a
            href="https://wa.me/+5493515395915?text=Quiero%20hacer%20un%20pedido"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-5"
          >
            Hacer un pedido
          </a>
        </nav>
      </div>
    </header>
  );
}

export default Navbar;
